import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useArealplan } from '@origo/planregister';
import PlanDetailShell, { type PlanTab } from './components/PlanDetailShell';
import PlanLandingContainer from './routes/landing/PlanLandingContainer';
import NewPlanContainer from './routes/new-plan/NewPlanContainer';
import PlanInfoContainer from './routes/plan-info/PlanInfoContainer';
import { rememberRecentPlan } from './utils/recentPlans';
import ProcessingsContainer from './routes/processings/ProcessingsContainer';
import RelationsContainer from './routes/relations/RelationsContainer';
import DocumentsContainer from './routes/documents/DocumentsContainer';
import DispensationsContainer from './routes/dispensations/DispensationsContainer';

type Route =
    | { kind: 'landing' }
    | { kind: 'new' }
    | { kind: 'plan'; napId: string; tab: Exclude<PlanTab, 'kart'> };

interface PlanregisterViewProps {
    /** Lar shell-en navigere brukeren tilbake til landing — typisk via
     *  klikk på logo/tittel i AppHeader. */
    onGoHomeRegister?: (handler: (() => void) | null) => void;
}

const PlanregisterView: React.FC<PlanregisterViewProps> = ({ onGoHomeRegister }) => {
    const [route, setRoute] = useState<Route>({ kind: 'landing' });

    // Plan-tittel hentes på view-nivå for shell-headeren. Samme query brukes
    // av PlanInfoContainer — React-Query deduplikerer på query-key, så dette
    // koster ingenting ekstra.
    const napId = route.kind === 'plan' ? route.napId : undefined;
    const { data: plan } = useArealplan(napId);

    // Shell-level «Lagre plan» speiler aktiv fane sin dirty-state og save-
    // handler. Aktiv container registrerer seg via callbacks; ref unngår
    // unødvendige re-renders, useState driver synligheten av shell-knappen.
    const [isPlanDirty, setIsPlanDirty] = useState(false);
    const saveHandlerRef = useRef<(() => void) | null>(null);

    const handleShellSavePlan = useCallback(() => {
        saveHandlerRef.current?.();
    }, []);

    const handleSaveHandlerChange = useCallback((handler: (() => void) | null) => {
        saveHandlerRef.current = handler;
    }, []);

    const handleSelectPlan = useCallback((selected: string) => {
        rememberRecentPlan(selected);
        setRoute({ kind: 'plan', napId: selected, tab: 'info' });
    }, []);

    const handleCreatePlan = useCallback(() => {
        setRoute({ kind: 'new' });
    }, []);

    const handlePlanCreated = useCallback((napId: string) => {
        rememberRecentPlan(napId);
        setRoute({ kind: 'plan', napId, tab: 'info' });
    }, []);

    const handleCancelCreate = useCallback(() => {
        setRoute({ kind: 'landing' });
    }, []);

    const handleTabChange = useCallback((tab: PlanTab) => {
        setRoute(prev => (prev.kind === 'plan' && tab !== 'kart' ? { ...prev, tab } : prev));
    }, []);

    const handleBackToMap = useCallback(() => {
        setRoute({ kind: 'landing' });
    }, []);

    // Registrer go-home-handler hos shell-en (AppLayout/AppHeader).
    useEffect(() => {
        onGoHomeRegister?.(handleBackToMap);
        return () => onGoHomeRegister?.(null);
    }, [onGoHomeRegister, handleBackToMap]);

    if (route.kind === 'landing') {
        return (
            <PlanLandingContainer
                onSelectPlan={handleSelectPlan}
                onCreatePlan={handleCreatePlan}
            />
        );
    }

    if (route.kind === 'new') {
        return (
            <NewPlanContainer
                onCreated={handlePlanCreated}
                onCancel={handleCancelCreate}
            />
        );
    }

    return (
        <PlanDetailShell
            activeTab={route.tab}
            onTabChange={handleTabChange}
            onBackToMap={handleBackToMap}
            planTitle={plan?.plannavn}
            onSavePlan={isPlanDirty ? handleShellSavePlan : undefined}
        >
            {route.tab === 'info' && (
                <PlanInfoContainer
                    napId={route.napId}
                    onDirtyChange={setIsPlanDirty}
                    onSaveHandlerChange={handleSaveHandlerChange}
                />
            )}
            {route.tab === 'behandlinger' && (
                <ProcessingsContainer napId={route.napId} />
            )}
            {route.tab === 'planforhold' && (
                <RelationsContainer napId={route.napId} />
            )}
            {route.tab === 'dokumenter' && (
                <DocumentsContainer napId={route.napId} />
            )}
            {route.tab === 'dispensasjoner' && (
                <DispensationsContainer napId={route.napId} />
            )}
        </PlanDetailShell>
    );
};

export default PlanregisterView;
